import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import { confirmLeave, getLeaveGuard } from '../lib/leaveGuard';
import { isIos } from '../utils/isIos';
// Shared with the Planning / Vidéothèque / Classement page titles.
import { VideoIcon, TrophyIcon, MessageIcon, UsersIcon, DumbbellIcon, MedalIcon } from './navIcons';
import { getStudentNavItems } from './student/studentNavItems';
import useCompactOnScroll from '../hooks/useCompactOnScroll';

/**
 * The unread pill on a nav item — one component for both nav variants so the
 * 99+ clamp and the styling cannot drift apart. `compact` is the student
 * icon-only nav (smaller, no ring); the default is the coach labelled nav.
 */
const NavBadge = ({ count, itemId, compact = false }) => (
  <span
    data-testid={`nav-badge-${itemId}`}
    className={`absolute flex items-center justify-center rounded-full bg-[var(--kaiylo-primary-hex)] font-bold text-white shadow-sm ${
      compact
        ? '-top-2 -right-2 h-[18px] min-w-[18px] px-1 text-[10px]'
        : '-top-1 -right-1 h-4 w-4 text-[9px] ring-1 ring-background'
    }`}
  >
    {count > 99 ? '99+' : count}
  </span>
);

// Student nav sizes come from the Figma frame (375px wide). They grow with the
// viewport so the pill keeps its proportions on large phones, floored at the
// design size and capped at +20% so desktop doesn't get a giant pill.
const NAV_DESIGN_WIDTH = 375;
const NAV_MAX_SCALE = 1.2;
const navSize = (px) =>
  `clamp(${px}px, ${((px / NAV_DESIGN_WIDTH) * 100).toFixed(3)}vw, ${px * NAV_MAX_SCALE}px)`;

// `newAnalysesCount`: unread coach analyses, supplied by MainLayout's wrapper
// (it reads the header notification registry; this component stays
// presentational and mountable outside MainLayout, e.g. the chat page).
const BottomNavBar = ({ relative = false, newAnalysesCount = 0 }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { unreadCount } = useNavigation();
  const { t } = useTranslation('common');
  // Pilule qui rapetisse en suivant le défilement (coach et élève), pas dans
  // la messagerie (barre en flux, sous la conversation).
  const shrinkRef = useCompactOnScroll((user?.role === 'coach' || user?.role === 'student') && !relative, location.pathname);

  const getNavItems = () => {
    if (!user) return [];

    if (user.role === 'coach') {
      return [
        { to: "/coach/dashboard", icon: UsersIcon, id: "clients", label: t('nav.clients') },
        { to: "/coach/exercises", icon: DumbbellIcon, id: "exercises", label: t('nav.exercises') },
        { to: "/coach/videotheque", icon: VideoIcon, id: "videos", label: t('nav.videos') },
        { to: "/coach/rankings", icon: TrophyIcon, id: "rankings", label: t('nav.rankings') },
        { to: "/coach/competitions", icon: MedalIcon, id: "competitions", label: t('nav.competitions') },
        { to: "/chat", icon: MessageIcon, id: "messages", label: t('nav.messages') },
      ];
    }

    if (user.role === 'student') {
      return getStudentNavItems(t);
    }
    // Add admin items here if needed
    return [];
  };

  const navItems = getNavItems();

  // Garde de sortie (lib/leaveGuard.js) : sans garde posée, le lien navigue
  // comme d'habitude. Avec une garde (ex. vidéo du récap pas partagée), on
  // attend la réponse de la page avant de naviguer. Les clics modifiés
  // (nouvel onglet) ne quittent pas la page et passent sans question.
  // Student tabs REPLACE the current entry instead of stacking one per tap,
  // like a native tab bar. The iPhone home-screen app now has a swipe-back
  // gesture that walks this history: with a pushed entry per tab, a swipe on
  // the dashboard's session cards went back to the previous tab instead, and
  // repeated swipes walked out to the boot / login entries (grey screen).
  // iPhone / iPad only: Android's back button keeps walking back through the
  // tabs as before (it had no such conflict).
  const replaceTab = user?.role === 'student' && isIos();

  const handleNavClick = (e, to) => {
    if (!getLeaveGuard() || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    confirmLeave().then((ok) => {
      if (ok) navigate(to, { replace: replaceTab });
    });
  };

  if (navItems.length === 0) {
    return null;
  }

  // Mobile inchangé (< md : toujours visible). Coach : la sidebar prend le
  // relais dès md. Élève : la pilule reste jusqu'à 1024px (tablette portrait),
  // puis StudentDesktopNav (barre latérale) la remplace.
  const navHideClass = user?.role === 'student' ? 'lg:hidden' : 'md:hidden';

  const renderNavItem = (item, options = {}) => {
    const { iconOnly = false } = options;
    // Coach sub-pages (Modèles, Questionnaires, Facturation) have no tab of
    // their own: they light up Clients, the home of the coach nav.
    const isActive =
      location.pathname === item.to ||
      (item.id === 'clients' && ['/coach/templates', '/coach/questionnaires', '/coach/facturation'].includes(location.pathname));
    // One badge slot per item, whichever nav variant renders it: unread
    // messages on the chat tab, unread coach analyses on the student
    // video-library tab (the coach nav has no such item, so it stays 0 there).
    const badgeCount =
      item.id === 'messages' ? unreadCount : item.id === 'video_library' ? newAnalysesCount : 0;

    if (iconOnly) {
      // Student nav (Figma 1675:6742): icons only, active item gets a filled
      // light circle behind a dark icon; inactive icons are muted grey.
      const activeCircleColor = '#0D0D0D';
      const activeIconColor = 'rgba(255, 255, 255, 0.95)';
      const inactiveIconColor = 'rgba(255, 255, 255, 0.25)';

      return (
        <NavLink
          key={item.to}
          to={item.to}
          replace={replaceTab}
          onClick={(e) => handleNavClick(e, item.to)}
          aria-label={item.label}
          className="flex items-center justify-center rounded-full relative"
          style={{ padding: `${navSize(10)} ${navSize(15)}` }}
        >
          {/* The active circle is ONE shared element (layoutId) rather than a
              per-item background: framer-motion then slides it from the old
              active item to the new one on route change instead of snapping. */}
          {isActive && (
            <motion.span
              layoutId="student-bottom-nav-active"
              aria-hidden="true"
              className="absolute inset-0 rounded-full"
              style={{ backgroundColor: activeCircleColor }}
              transition={{ type: 'spring', stiffness: 520, damping: 40 }}
            />
          )}
          <div className="relative z-10 flex items-center justify-center">
            <item.icon
              aria-hidden="true"
              className="transition-colors duration-200"
              style={{
                width: navSize(21),
                height: navSize(21),
                color: isActive ? activeIconColor : inactiveIconColor,
              }}
            />
            {badgeCount > 0 && <NavBadge count={badgeCount} itemId={item.id} compact />}
          </div>
        </NavLink>
      );
    }

    const activeColor = 'rgba(255, 255, 255, 1)';
    const inactiveColor = 'rgba(255, 255, 255, 0.25)';

    return (
      <NavLink
        key={item.to}
        to={item.to}
        replace={replaceTab}
        onClick={(e) => handleNavClick(e, item.to)}
        className="flex flex-col items-center justify-center gap-1 transition-colors relative"
        style={{ color: isActive ? activeColor : inactiveColor }}
      >
        <div className="relative">
          <item.icon
            className="h-6 w-6"
            style={{
              color: isActive ? activeColor : inactiveColor
            }}
          />
          {badgeCount > 0 && <NavBadge count={badgeCount} itemId={item.id} />}
        </div>
        <span
          className="text-xs"
          style={{
            color: isActive ? activeColor : inactiveColor,
            fontWeight: 400
          }}
        >
          {item.label}
        </span>
      </NavLink>
    );
  };

  // Floating rounded pill per the student Figma design — the coach's phone
  // layout now wears it too. The coach bar is `md:hidden` (the sidebar takes
  // over from md up), so this never reaches the coach desktop. The labelled
  // edge-pinned bar below is kept for any other role.
  const usesPill = user?.role === 'student' || user?.role === 'coach';

  if (usesPill) {
    return (
      <nav
        className={
          relative
            ? `${navHideClass} relative flex justify-center pointer-events-none`
            : `${navHideClass} fixed left-0 right-0 z-50 flex justify-center pointer-events-none`
        }
        style={
          relative
            ? undefined
            : { bottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }
        }
      >
        <div
          // Taille pilotée par le défilement (transform posé par le hook, sans relayout).
          ref={shrinkRef}
          className="flex items-center rounded-full backdrop-blur-[10px] pointer-events-auto origin-bottom will-change-transform"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.25)',
            boxShadow: '0px 0px 25px 5px rgba(255, 255, 255, 0.15)',
            padding: `${navSize(4)} ${navSize(5)}`,
            gap: navSize(6),
          }}
        >
          {navItems.map((item) => renderNavItem(item, { iconOnly: true }))}
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={relative ? `${navHideClass} relative bg-background` : `${navHideClass} fixed bottom-0 left-0 right-0 bg-background z-50`}
      style={{
        backgroundColor: 'rgba(13, 13, 13, 1)',
        borderTopWidth: '0px',
        borderTopColor: 'rgba(0, 0, 0, 0)',
        borderTopStyle: 'none',
        borderImage: 'none'
      }}
    >
      <div className="flex justify-around items-center h-16">
        {navItems.map((item) => renderNavItem(item))}
      </div>
    </nav>
  );
};

export default BottomNavBar;
